import { doc, setDoc, collection, addDoc, updateDoc, increment, getDoc } from 'firebase/firestore';
import { db } from '../../../core/config/firebase';

/**
 * Save a game score to Firestore
 * @param {string} userId - The user's Firebase Auth ID
 * @param {Object} scoreData - The score data to save
 * @returns {Promise<string>} - The ID of the created score document
 */
export async function saveScore(userId, scoreData) {
  try {
    const {
      username,
      targetWord,
      guesses,
      won,
      attempts,
      completionTimeMs,
      timestamp = new Date(),
      isDaily = false,
      dailyWordDate = null
    } = scoreData;

    // Validate required fields
    if (!userId || !username || !targetWord || attempts === undefined || won === undefined) {
      throw new Error('Missing required score data');
    }

    // Create score document in scores collection (with denormalized username)
    const scoresRef = collection(db, 'scores');
    const scoreDoc = await addDoc(scoresRef, {
      userId,
      username, // Denormalized for fast leaderboard queries
      targetWord,
      guesses: guesses || [],
      won,
      attempts,
      completionTimeMs: completionTimeMs || 0,
      timestamp,
      createdAt: new Date(),
      isDaily,
      dailyWordDate
    });

    console.log('Score saved successfully:', scoreDoc.id);
    return scoreDoc.id;
  } catch (error) {
    console.error('Error saving score:', error);
    throw error;
  }
}

/**
 * Update user stats after a game
 * @param {string} userId - The user's Firebase Auth ID
 * @param {boolean} won - Whether the user won the game
 * @param {number} attempts - Number of attempts (1-6 for won, 7 for lost)
 * @returns {Promise<void>}
 */
export async function updateUserStats(userId, won, attempts) {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }

    const currentStats = userDoc.data().stats || {
      gamesPlayed: 0,
      gamesWon: 0,
      gamesLost: 0,
      currentStreak: 0,
      maxStreak: 0,
      totalAttempts: 0,
      avgScore: 0
    };

    // Calculate new stats
    const newGamesPlayed = currentStats.gamesPlayed + 1;
    const newGamesWon = currentStats.gamesWon + (won ? 1 : 0);
    const newGamesLost = currentStats.gamesLost + (won ? 0 : 1);

    // Update streak
    let newCurrentStreak = won ? (currentStats.currentStreak || 0) + 1 : 0;
    let newMaxStreak = Math.max(newCurrentStreak, currentStats.maxStreak || 0);

    // Calculate new average (only count won games for average)
    const newTotalAttempts = currentStats.totalAttempts + (won ? attempts : 0);
    const newAvgScore = newGamesWon > 0 ? newTotalAttempts / newGamesWon : 0;

    // Update user stats
    await updateDoc(userRef, {
      'stats.gamesPlayed': newGamesPlayed,
      'stats.gamesWon': newGamesWon,
      'stats.gamesLost': newGamesLost,
      'stats.currentStreak': newCurrentStreak,
      'stats.maxStreak': newMaxStreak,
      'stats.totalAttempts': newTotalAttempts,
      'stats.avgScore': Math.round(newAvgScore * 100) / 100, // Round to 2 decimals
      'stats.lastPlayed': new Date()
    });

    console.log('User stats updated successfully');
  } catch (error) {
    console.error('Error updating user stats:', error);
    throw error;
  }
}

/**
 * Save game result (combines score saving and stats update)
 * @param {string} userId - The user's Firebase Auth ID
 * @param {Object} gameResult - Complete game result data
 * @returns {Promise<string>} - The ID of the created score document
 */
export async function saveGameResult(userId, gameResult) {
  try {
    const {
      username,
      targetWord,
      guesses,
      won,
      attempts,
      completionTimeMs,
      isDaily,
      dailyWordDate
    } = gameResult;

    // Save the score (with denormalized username)
    const scoreId = await saveScore(userId, {
      username,
      targetWord,
      guesses,
      won,
      attempts,
      completionTimeMs,
      timestamp: new Date(),
      isDaily,
      dailyWordDate
    });

    // Update user stats
    await updateUserStats(userId, won, attempts);

    return scoreId;
  } catch (error) {
    console.error('Error saving game result:', error);
    throw error;
  }
}

/**
 * Get a single score by ID with full details including guesses
 * @param {string} scoreId - The score document ID
 * @returns {Promise<Object>} - Score document with all fields
 */
export async function getScoreById(scoreId) {
  try {
    const scoreRef = doc(db, 'scores', scoreId);
    const scoreDoc = await getDoc(scoreRef);

    if (!scoreDoc.exists()) {
      throw new Error('Score not found');
    }

    return {
      id: scoreDoc.id,
      ...scoreDoc.data()
    };
  } catch (error) {
    console.error('Error fetching score:', error);
    throw error;
  }
}

/**
 * Get user's game history
 * @param {string} userId - The user's Firebase Auth ID
 * @param {number} limit - Maximum number of scores to retrieve
 * @returns {Promise<Array>} - Array of score documents
 */
export async function getUserScores(userId, limit = 10) {
  try {
    const { query, collection, where, orderBy, limit: firestoreLimit, getDocs } = await import('firebase/firestore');

    const scoresRef = collection(db, 'scores');
    const q = query(
      scoresRef,
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limit)
    );

    const querySnapshot = await getDocs(q);
    const scores = [];

    querySnapshot.forEach((doc) => {
      scores.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return scores;
  } catch (error) {
    console.error('Error fetching user scores:', error);
    throw error;
  }
}

/**
 * Get leaderboard scores with ranking
 * Ranks by: 1) Fewest attempts, 2) Fastest time (tiebreaker), 3) Tie if same attempts and time
 * @param {number} limit - Maximum number of scores to retrieve
 * @returns {Promise<Array>} - Array of leaderboard entries with user info
 */
export async function getLeaderboard(limit = 50) {
  try {
    const { query, collection, where, orderBy, limit: firestoreLimit, getDocs } = await import('firebase/firestore');
    const { doc, getDoc } = await import('firebase/firestore');

    // Get all winning scores, ordered by attempts then time
    const scoresRef = collection(db, 'scores');
    const q = query(
      scoresRef,
      where('won', '==', true),
      orderBy('attempts', 'asc'),
      orderBy('completionTimeMs', 'asc'),
      firestoreLimit(limit)
    );

    const querySnapshot = await getDocs(q);
    const leaderboardData = [];

    // Fetch user profiles for each score
    for (const scoreDoc of querySnapshot.docs) {
      const scoreData = scoreDoc.data();

      // Get user profile to fetch username
      const userRef = doc(db, 'users', scoreData.userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        leaderboardData.push({
          id: scoreDoc.id,
          username: userData.username,
          attempts: scoreData.attempts,
          completionTimeMs: scoreData.completionTimeMs,
          timestamp: scoreData.timestamp,
          targetWord: scoreData.targetWord
        });
      }
    }

    // Calculate rankings with tie handling
    const rankedLeaderboard = [];
    let currentRank = 1;
    let prevAttempts = null;
    let prevTime = null;

    for (let i = 0; i < leaderboardData.length; i++) {
      const entry = leaderboardData[i];

      // Check if current entry is tied with previous
      const isTied = prevAttempts === entry.attempts && prevTime === entry.completionTimeMs;

      // Only increment rank if not tied
      if (!isTied && i > 0) {
        currentRank = i + 1;
      }

      rankedLeaderboard.push({
        ...entry,
        rank: currentRank,
        isTied
      });

      prevAttempts = entry.attempts;
      prevTime = entry.completionTimeMs;
    }

    return rankedLeaderboard;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }
}

/**
 * Get today's leaderboard (scores from last 24 hours)
 * @param {number} limit - Maximum number of scores to retrieve
 * @returns {Promise<Array>} - Array of today's leaderboard entries
 */
export async function getTodayLeaderboard(limit = 50) {
  try {
    const { query, collection, where, orderBy, limit: firestoreLimit, getDocs } = await import('firebase/firestore');
    const { doc, getDoc } = await import('firebase/firestore');

    // Get timestamp for 24 hours ago
    const oneDayAgo = new Date();
    oneDayAgo.setHours(oneDayAgo.getHours() - 24);

    // Get all winning scores from last 24 hours
    // Note: We can only orderBy timestamp because we have a range filter on it
    const scoresRef = collection(db, 'scores');
    const q = query(
      scoresRef,
      where('won', '==', true),
      where('timestamp', '>=', oneDayAgo),
      orderBy('timestamp', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const leaderboardData = [];

    // Fetch user profiles for each score
    for (const scoreDoc of querySnapshot.docs) {
      const scoreData = scoreDoc.data();

      // Get user profile to fetch username
      const userRef = doc(db, 'users', scoreData.userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        leaderboardData.push({
          id: scoreDoc.id,
          username: userData.username,
          attempts: scoreData.attempts,
          completionTimeMs: scoreData.completionTimeMs,
          timestamp: scoreData.timestamp,
          targetWord: scoreData.targetWord
        });
      }
    }

    // Sort client-side by attempts (ascending) then by time (ascending)
    leaderboardData.sort((a, b) => {
      if (a.attempts !== b.attempts) {
        return a.attempts - b.attempts;
      }
      return a.completionTimeMs - b.completionTimeMs;
    });

    // Limit the results
    const limitedData = leaderboardData.slice(0, limit);

    // Calculate rankings with tie handling
    const rankedLeaderboard = [];
    let currentRank = 1;
    let prevAttempts = null;
    let prevTime = null;

    for (let i = 0; i < limitedData.length; i++) {
      const entry = limitedData[i];

      // Check if current entry is tied with previous
      const isTied = prevAttempts === entry.attempts && prevTime === entry.completionTimeMs;

      // Only increment rank if not tied
      if (!isTied && i > 0) {
        currentRank = i + 1;
      }

      rankedLeaderboard.push({
        ...entry,
        rank: currentRank,
        isTied
      });

      prevAttempts = entry.attempts;
      prevTime = entry.completionTimeMs;
    }

    return rankedLeaderboard;
  } catch (error) {
    console.error('Error fetching today leaderboard:', error);
    throw error;
  }
}

/**
 * Get leaderboard for a specific daily word challenge
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {number} limit - Maximum number of scores to retrieve
 * @returns {Promise<Array>} - Array of leaderboard entries for that day's challenge
 */
export async function getDailyLeaderboard(dateString, limit = 50) {
  try {
    const { query, collection, where, getDocs } = await import('firebase/firestore');

    // Get all winning scores for this specific daily challenge
    const scoresRef = collection(db, 'scores');
    const q = query(
      scoresRef,
      where('isDaily', '==', true),
      where('dailyWordDate', '==', dateString),
      where('won', '==', true)
    );

    const querySnapshot = await getDocs(q);
    const leaderboardData = [];

    // Map score data directly (username is denormalized in score doc)
    querySnapshot.forEach((scoreDoc) => {
      const scoreData = scoreDoc.data();
      leaderboardData.push({
        id: scoreDoc.id,
        username: scoreData.username, // Denormalized username
        attempts: scoreData.attempts,
        completionTimeMs: scoreData.completionTimeMs,
        timestamp: scoreData.timestamp,
        targetWord: scoreData.targetWord
      });
    });

    // Sort client-side by attempts (ascending) then by time (ascending)
    leaderboardData.sort((a, b) => {
      if (a.attempts !== b.attempts) {
        return a.attempts - b.attempts;
      }
      return a.completionTimeMs - b.completionTimeMs;
    });

    // Limit the results
    const limitedData = leaderboardData.slice(0, limit);

    // Calculate rankings with tie handling
    const rankedLeaderboard = [];
    let currentRank = 1;
    let prevAttempts = null;
    let prevTime = null;

    for (let i = 0; i < limitedData.length; i++) {
      const entry = limitedData[i];

      // Check if current entry is tied with previous
      const isTied = prevAttempts === entry.attempts && prevTime === entry.completionTimeMs;

      // Only increment rank if not tied
      if (!isTied && i > 0) {
        currentRank = i + 1;
      }

      rankedLeaderboard.push({
        ...entry,
        rank: currentRank,
        isTied
      });

      prevAttempts = entry.attempts;
      prevTime = entry.completionTimeMs;
    }

    return rankedLeaderboard;
  } catch (error) {
    console.error('Error fetching daily leaderboard:', error);
    throw error;
  }
}

/**
 * Get this week's leaderboard (Monday-Sunday)
 * Shows one appearance per user (their best score this week)
 * @param {number} limit - Maximum number of entries to retrieve
 * @returns {Promise<Array>} - Array of leaderboard entries
 */
export async function getWeeklyLeaderboard(limit = 50) {
  try {
    const { query, collection, where, getDocs, Timestamp } = await import('firebase/firestore');

    // Calculate start of week (Monday in Eastern Time)
    const now = new Date();
    const easternNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const dayOfWeek = easternNow.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so go back 6 days

    const mondayDate = new Date(easternNow);
    mondayDate.setDate(easternNow.getDate() - daysToMonday);
    mondayDate.setHours(0, 0, 0, 0);

    // Convert to Firestore Timestamp
    const mondayTimestamp = Timestamp.fromDate(mondayDate);

    console.log('📅 Weekly leaderboard query - Start of week:', mondayDate.toISOString());

    // Get all winning scores from this week
    const scoresRef = collection(db, 'scores');
    const q = query(
      scoresRef,
      where('won', '==', true),
      where('isDaily', '==', true),
      where('timestamp', '>=', mondayTimestamp)
    );

    const querySnapshot = await getDocs(q);
    console.log('📊 Weekly scores found:', querySnapshot.size);
    const userBestScores = new Map(); // userId -> best score

    // Process each score and keep only the best per user
    querySnapshot.forEach((scoreDoc) => {
      const scoreData = scoreDoc.data();
      const userId = scoreData.userId;

      // Check if this is better than user's current best
      const existingBest = userBestScores.get(userId);

      if (!existingBest ||
          scoreData.attempts < existingBest.attempts ||
          (scoreData.attempts === existingBest.attempts && scoreData.completionTimeMs < existingBest.completionTimeMs)) {
        userBestScores.set(userId, {
          id: scoreDoc.id,
          userId,
          username: scoreData.username, // Denormalized username
          attempts: scoreData.attempts,
          completionTimeMs: scoreData.completionTimeMs,
          timestamp: scoreData.timestamp,
          targetWord: scoreData.targetWord,
          dailyWordDate: scoreData.dailyWordDate
        });
      }
    });

    // Convert map to array (usernames already included)
    const leaderboardData = Array.from(userBestScores.values());

    // Sort by attempts then time
    leaderboardData.sort((a, b) => {
      if (a.attempts !== b.attempts) {
        return a.attempts - b.attempts;
      }
      return a.completionTimeMs - b.completionTimeMs;
    });

    // Limit results
    const limitedData = leaderboardData.slice(0, limit);

    // Add rankings with tie handling
    const rankedLeaderboard = [];
    let currentRank = 1;
    let prevAttempts = null;
    let prevTime = null;

    for (let i = 0; i < limitedData.length; i++) {
      const entry = limitedData[i];
      const isTied = prevAttempts === entry.attempts && prevTime === entry.completionTimeMs;

      if (!isTied && i > 0) {
        currentRank = i + 1;
      }

      rankedLeaderboard.push({
        ...entry,
        rank: currentRank,
        isTied
      });

      prevAttempts = entry.attempts;
      prevTime = entry.completionTimeMs;
    }

    console.log('✅ Weekly leaderboard ready:', rankedLeaderboard.length, 'entries');
    return rankedLeaderboard;
  } catch (error) {
    console.error('❌ Error fetching weekly leaderboard:', error);
    console.error('Error details:', error.message, error.code);
    throw error;
  }
}

/**
 * Get all-time best players leaderboard
 * Ranked by average guesses (lower is better)
 * OPTIMIZED: Uses denormalized stats from user profiles (no N+1 queries!)
 * @param {number} limit - Maximum number of entries to retrieve
 * @param {number} minGames - Minimum games required to appear (default: 1, TODO: change to 10 after testing)
 * @returns {Promise<Array>} - Array of player stats
 */
export async function getBestPlayersLeaderboard(limit = 50, minGames = 1) {
  try {
    const { query, collection, where, getDocs, orderBy } = await import('firebase/firestore');

    // Query users who have played at least minGames
    // Note: We can't orderBy avgScore with a range filter on gamesWon, so we order by gamesWon and sort in memory
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('stats.gamesWon', '>=', minGames),
      orderBy('stats.gamesWon', 'asc')
      // We'll sort by avgScore in memory after fetching
    );

    const usersSnapshot = await getDocs(q);
    const playerStats = [];

    // Map user data directly from their stats
    usersSnapshot.forEach((userDoc) => {
      const userData = userDoc.data();
      const stats = userData.stats || {};

      // Only include users with valid stats
      if (stats.gamesWon >= minGames && stats.avgScore > 0) {
        const totalGames = stats.gamesPlayed || 0;
        const gamesWon = stats.gamesWon || 0;
        const winRate = totalGames > 0 ? (gamesWon / totalGames) * 100 : 0;

        playerStats.push({
          username: userData.username,
          avgGuesses: stats.avgScore, // Already rounded in updateUserStats
          gamesWon: gamesWon,
          totalGames: totalGames,
          winRate: Math.round(winRate),
          currentStreak: stats.currentStreak || 0,
          maxStreak: stats.maxStreak || 0
        });
      }
    });

    // Sort by average guesses (lower is better), then by games won (higher is better)
    playerStats.sort((a, b) => {
      if (a.avgGuesses !== b.avgGuesses) {
        return a.avgGuesses - b.avgGuesses;
      }
      return b.gamesWon - a.gamesWon; // Tiebreaker: more games won
    });

    // Limit to requested number of entries
    const limitedStats = playerStats.slice(0, limit);

    // Add rankings with tie handling
    const rankedLeaderboard = [];
    let currentRank = 1;
    let prevAvg = null;

    for (let i = 0; i < limitedStats.length; i++) {
      const entry = limitedStats[i];
      const isTied = prevAvg === entry.avgGuesses;

      if (!isTied && i > 0) {
        currentRank = i + 1;
      }

      rankedLeaderboard.push({
        ...entry,
        rank: currentRank,
        isTied
      });

      prevAvg = entry.avgGuesses;
    }

    return rankedLeaderboard;
  } catch (error) {
    console.error('Error fetching best players leaderboard:', error);
    throw error;
  }
}

/**
 * Get all-time best scores leaderboard
 * Shows individual game performances (users can appear multiple times)
 * @param {number} limit - Maximum number of entries to retrieve
 * @returns {Promise<Array>} - Array of best individual scores
 */
export async function getBestScoresLeaderboard(limit = 50) {
  try {
    const { query, collection, where, orderBy, limit: firestoreLimit, getDocs } = await import('firebase/firestore');

    // Get top winning daily scores ordered by attempts then time
    // Note: This requires a composite index on (won, isDaily, attempts, completionTimeMs)
    const scoresRef = collection(db, 'scores');
    const q = query(
      scoresRef,
      where('won', '==', true),
      where('isDaily', '==', true),
      orderBy('attempts', 'asc'),
      orderBy('completionTimeMs', 'asc'),
      firestoreLimit(limit)
    );

    const querySnapshot = await getDocs(q);
    const allScores = [];

    // Map score data directly (username is denormalized in score doc)
    querySnapshot.forEach((scoreDoc) => {
      const scoreData = scoreDoc.data();
      allScores.push({
        id: scoreDoc.id,
        username: scoreData.username, // Denormalized username
        attempts: scoreData.attempts,
        completionTimeMs: scoreData.completionTimeMs,
        timestamp: scoreData.timestamp,
        targetWord: scoreData.targetWord,
        dailyWordDate: scoreData.dailyWordDate
      });
    });

    // Add rankings with tie handling
    const rankedLeaderboard = [];
    let currentRank = 1;
    let prevAttempts = null;
    let prevTime = null;

    for (let i = 0; i < allScores.length; i++) {
      const entry = allScores[i];
      const isTied = prevAttempts === entry.attempts && prevTime === entry.completionTimeMs;

      if (!isTied && i > 0) {
        currentRank = i + 1;
      }

      rankedLeaderboard.push({
        ...entry,
        rank: currentRank,
        isTied
      });

      prevAttempts = entry.attempts;
      prevTime = entry.completionTimeMs;
    }

    return rankedLeaderboard;
  } catch (error) {
    console.error('Error fetching best scores leaderboard:', error);
    throw error;
  }
}
